/**
 * Historie: koppen die tijdens het scrollen doorbuigen en weer vlak trekken.
 *
 * Elk historie-item is een eigen scherm. De foto, de titel en de tekst lopen gewoon met
 * de pagina mee; alleen het jaartal is per letter opgeknipt -- dat is altijd vier tekens
 * en dus altijd een regel, waar een titel van een halve zin dat nooit is.
 *
 * Elke letter hangt aan zijn eigen veer die dezelfde scrollpositie najaagt, maar hoe
 * verder een letter van het midden van zijn regel staat, hoe zwaarder hij gedempt is.
 * De buitenste letters lopen daardoor achter op de binnenste en dat is wat je als boog
 * ziet. Sta je stil, dan komt alles op dezelfde plek uit en is de regel weer kaarsrecht
 * -- de boog is dus een gevolg van na-ijling, niet een vorm die we tekenen.
 *
 * De rotatie per letter is niet apart geanimeerd maar afgeleid: elke letter gaat op de
 * raaklijn staan door zijn twee buren. Daardoor draaien de uiteinden vanzelf minder dan
 * het midden van de flank, precies zoals bij een echte doorbuigende regel.
 *
 * De constanten komen uit een frame-voor-frame meting van de referentievideo: per
 * lettervorm is het verticale spoor getrackt en daar is een gedempte veer op gefit. Alle
 * letters kwamen op dezelfde eigenfrequentie uit (~10,8 rad/s, oftewel een uitslingering
 * van ~0,6s); alleen de demping loopt op met de afstand tot het midden.
 *
 * Wat de meting niet dekt: op het hoogtepunt van een snelle scroll boog het origineel
 * scherper door dan dit model. Dat komt vermoedelijk doordat het meetsignaal zelf al
 * gedempt was, waardoor de gemeten piek te vlak is. STRENGTH is de knop om dat bij te
 * stellen zonder aan de rest te rekenen.
 */

import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { getLenis } from './smooth-scroll';

gsap.registerPlugin(SplitText);

// Eigenfrequentie van de veer, gelijk voor elke letter.
const OMEGA = 10.8;

// Demping van de middelste letter, plus wat er per index-stap naar buiten bij komt.
const ZETA_BASE = 0.24;
const ZETA_SLOPE = 0.055;

// De kop in de video was 'Italian Negroni': veertien letters, dus de buitenste stond 6,5
// stappen van het midden. Een jaartal is vier cijfers en komt niet verder dan 1,5 stap,
// en zou met dezelfde helling nauwelijks buigen -- het blok ijlt dan wel na, maar als
// geheel. Door de helling op deze spreiding te normaliseren krijgt de buitenste letter
// altijd de gemeten demping, ongeacht hoe lang het woord is.
const REFERENCE_SPREAD = 6.5;

// Schaal op de na-ijling. 1 = zoals gemeten; hoger buigt dieper door.
const STRENGTH = 1;

// De regelhoogte waarop de video is opgemeten (kop van 126px). De uitslag van de veer
// hangt aan de scrollsnelheid en niet aan de lettergrootte, dus zonder correctie krijgt
// een kleine kop dezelfde uitslag in pixels als een grote -- en dan buigt hij veel
// verder door dan zijn eigen regelhoogte.
const REFERENCE_LINE_HEIGHT = 128;

// De kop mag niet over de kop slaan bij een ruk aan de scrollbar.
const ROT_MAX = 32;

// Onder deze drempel staat alles stil en hoeven we niets meer te schrijven.
const REST_EPS = 0.03;

// Onder md is de kop te klein en te veel afgebroken om te buigen; daar blijft het vlak.
const MIN_WIDTH = 768;

export default function initHistoryArc() {
  const stages = document.querySelectorAll('[data-history-stage]');

  if (! stages.length) {
    return;
  }

  // Wie aangeeft minder beweging te willen, krijgt gewoon rechte koppen.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  const readScroll = () => {
    const lenis = getLenis();

    // Lenis houdt de gedempte scrollwaarde bij. Die uitlezen scheelt een layout-read
    // per tick en het is bovendien dezelfde waarde waar de rest van de pagina op loopt.
    return lenis ? lenis.scroll : window.scrollY;
  };

  const entries = [];

  stages.forEach((stage) => {
    const arcTarget = stage.querySelector('[data-history-arc]');

    if (! arcTarget) {
      return;
    }

    const entry = { stage, groups: [], top: 0, bottom: 0 };
    entries.push(entry);

    // Regels én letters: de letters buigen, de regels houden de kop heel bij het
    // afbreken. Elke regel krijgt zijn eigen midden, zodat een kop van twee regels
    // twee bogen wordt in plaats van een scheve.
    new SplitText(arcTarget, {
      type: 'lines, chars',
      autoSplit: true,
      linesClass: 'history__line',
      charsClass: 'history__char',
      onSplit(self) {
        const target = -readScroll();

        entry.groups = self.lines.map((line) => {
          const chars = [...line.querySelectorAll('.history__char')];
          const middle = (chars.length - 1) / 2;
          const spread = middle > 0 ? REFERENCE_SPREAD / middle : 0;

          const items = chars.map((el, index) => ({
            el,
            setY: gsap.quickSetter(el, 'y', 'px'),
            setRot: gsap.quickSetter(el, 'rotation', 'deg'),
            zeta: ZETA_BASE + ZETA_SLOPE * Math.abs(index - middle) * spread,

            // Het horizontale midden binnen de regel; alleen de onderlinge afstand
            // telt, want daar wordt de raaklijn mee bepaald.
            cx: el.offsetLeft + el.offsetWidth / 2,

            // Op de huidige scrollpositie beginnen, anders springt de kop bij het laden.
            x: target,
            v: 0,
          }));

          // Twee correcties op de uitslag. Eén: meeschalen met de regelhoogte, anders
          // buigt een kleine kop even ver door als een grote. Twee: delen door het
          // aantal regels, want twee regels die allebei de volle uitslag maken lopen
          // dwars door elkaar heen -- in de referentievideo was de kop altijd één regel,
          // dus dat probleem bestond daar niet.
          const lineHeight = parseFloat(getComputedStyle(line).lineHeight)
            || parseFloat(getComputedStyle(line).fontSize) * 1.12;
          const amp = Math.min(1, lineHeight / REFERENCE_LINE_HEIGHT / self.lines.length);

          // De na-ijling loopt op per lettervolgnummer, maar de raaklijn rekent met de
          // werkelijke afstand tussen de letters. Bij een smal paar -- 'ij', 'lf', en in
          // het Nederlands kom je die overal tegen -- is die afstand klein terwijl het
          // hoogteverschil even groot blijft, en schiet de hoek uit. Een ondergrens van
          // een halve nominale stap houdt dat binnen de perken zonder de rest te raken.
          const span = items.length > 1 ? items[items.length - 1].cx - items[0].cx : 0;
          const advance = items.length > 1 ? span / (items.length - 1) : 0;

          return { chars: items, minDx: advance, amp };
        });

        // Bij een resize herberekent SplitText de regels; de opgeslagen hoogte van het
        // scherm klopt dan niet meer.
        measure();

        return gsap.context(() => {}, arcTarget);
      },
    });
  });

  if (! entries.length) {
    return;
  }

  function measure() {
    for (const entry of entries) {
      const rect = entry.stage.getBoundingClientRect();
      const scroll = readScroll();

      entry.top = rect.top + scroll;
      entry.bottom = entry.top + rect.height;
    }
  }

  measure();
  window.addEventListener('resize', measure);

  // De foto's laden lui, dus een scherm wordt hoger zodra er een binnenkomt. Zonder
  // hermeten blijven de opgeslagen grenzen van voor die groei staan en valt de
  // zichtbaarheidstoets op de verkeerde plek -- een kop wordt dan geparkeerd terwijl hij
  // in beeld staat, of andersom.
  if (typeof ResizeObserver !== 'undefined') {
    const observer = new ResizeObserver(measure);

    for (const entry of entries) {
      observer.observe(entry.stage);
    }
  }

  let narrow = false;

  gsap.ticker.add((time, deltaMs) => {
    if (window.innerWidth < MIN_WIDTH) {
      // Van breed naar smal getrokken: de letters staan dan nog scheef van de laatste
      // uitslag. Eén keer platleggen en daarna niets meer doen.
      if (! narrow) {
        narrow = true;

        for (const entry of entries) {
          entry.parked = false;

          for (const group of entry.groups) {
            for (const char of group.chars) {
              char.v = 0;
              char.setY(0);
              char.setRot(0);
            }
          }
        }
      }

      return;
    }

    if (narrow) {
      narrow = false;

      // Terug naar breed: op de huidige scrollpositie beginnen in plaats van op een
      // stand van voor de resize.
      for (const entry of entries) {
        for (const group of entry.groups) {
          for (const char of group.chars) {
            char.x = -readScroll();
            char.v = 0;
          }
        }
      }
    }

    const scroll = readScroll();
    const target = -scroll;
    const viewportBottom = scroll + window.innerHeight;

    // Bij een hapering of een tabblad dat terugkomt is dt groot; de veer zou dan in één
    // stap doorschieten. Vaste substappen houden hem stabiel.
    const dt = Math.min(deltaMs / 1000, 1 / 20);
    const steps = Math.max(1, Math.ceil(dt / (1 / 240)));
    const stepDt = dt / steps;
    const omega2 = OMEGA * OMEGA;

    for (const entry of entries) {
      const offscreen = entry.bottom < scroll - window.innerHeight
        || entry.top > viewportBottom + window.innerHeight;

      // Wat ruim buiten beeld staat hoeft niet te bewegen; scheelt style-writes. Wel
      // eerst platleggen: sla je de veer midden in een uitslag over, dan bevriest de kop
      // scheef en staat hij zo weer in beeld als je terugscrollt.
      if (offscreen) {
        if (! entry.parked) {
          entry.parked = true;

          for (const group of entry.groups) {
            for (const char of group.chars) {
              char.x = target;
              char.v = 0;
              char.setY(0);
              char.setRot(0);
            }
          }
        }

        continue;
      }

      // Net weer in beeld: op de huidige scrollpositie beginnen in plaats van op de
      // stand van voor het parkeren, anders schiet de kop bij binnenkomst uit.
      if (entry.parked) {
        entry.parked = false;

        for (const group of entry.groups) {
          for (const char of group.chars) {
            char.x = target;
            char.v = 0;
          }
        }
      }

      for (const group of entry.groups) {
        const chars = group.chars;
        const amp = group.amp * STRENGTH;
        let moving = false;

        for (const char of chars) {
          for (let step = 0; step < steps; step++) {
            const accel = omega2 * (target - char.x) - 2 * char.zeta * OMEGA * char.v;

            char.v += accel * stepDt;
            char.x += char.v * stepDt;
          }

          if (Math.abs(char.x - target) > REST_EPS || Math.abs(char.v) > REST_EPS) {
            moving = true;
          }
        }

        if (! moving) {
          continue;
        }

        const count = chars.length;

        for (let index = 0; index < count; index++) {
          const char = chars[index];
          const before = chars[Math.max(0, index - 1)];
          const after = chars[Math.min(count - 1, index + 1)];
          const dx = Math.max(after.cx - before.cx, group.minDx);

          // De raaklijn door de buren. Eén letter op een regel heeft geen buren en
          // blijft dus rechtop staan.
          const rotation = dx === 0
            ? 0
            : Math.atan2((after.x - before.x) * amp, dx) * 180 / Math.PI;

          char.setY((char.x - target) * amp);
          char.setRot(Math.max(-ROT_MAX, Math.min(ROT_MAX, rotation)));
        }
      }
    }
  });
}
